import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Auth0Context, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import {
  applyAuthReturnTo,
  hasAuth0RedirectCallbackParams,
} from '../utils/auth0Errors';

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const auth0Audience = process.env.REACT_APP_AUTH0_AUDIENCE;

export function getPlaywrightAuth0Mock() {
  if (typeof window === 'undefined') {
    return null;
  }

  const mock = window.__PLAYWRIGHT_AUTH0__;
  return mock && typeof mock === 'object' ? mock : null;
}

export function AuthReturnLoading() {
  return (
    <div
      data-testid="auth-return-loading"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f7fafc',
        color: '#2d3748',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div role="status" aria-live="polite" aria-label="Loading">
        Loading…
      </div>
    </div>
  );
}

export function AuthRedirectGate({ children }) {
  const { isLoading } = useAuth0();
  const [callbackInFlight, setCallbackInFlight] = useState(() => (
    typeof window !== 'undefined'
    && hasAuth0RedirectCallbackParams(window.location.search)
  ));

  useEffect(() => {
    if (!isLoading && callbackInFlight) {
      setCallbackInFlight(false);
    }
  }, [callbackInFlight, isLoading]);

  // Neutral shell while Auth0 finishes session/callback handling, and for one settle
  // pass after a callback so returnTo navigation can commit before app routes paint.
  if (isLoading || callbackInFlight) {
    return <AuthReturnLoading />;
  }

  return children;
}

export function MockAuth0Provider({ children }) {
  const mock = useMemo(() => {
    const source = getPlaywrightAuth0Mock() || {};

    return {
      error: undefined,
      isAuthenticated: Boolean(source.isAuthenticated),
      isLoading: false,
      user: source.user || null,
      loginWithRedirect:
        source.loginWithRedirect
        || (async () => undefined),
      logout:
        source.logout
        || (() => undefined),
      getAccessTokenSilently:
        source.getAccessTokenSilently
        || (async () => source.token || 'playwright-token'),
      getAccessTokenWithPopup:
        source.getAccessTokenWithPopup
        || (async () => source.token || 'playwright-token'),
      getIdTokenClaims:
        source.getIdTokenClaims
        || (async () => null),
      loginWithPopup:
        source.loginWithPopup
        || (async () => undefined),
      handleRedirectCallback:
        source.handleRedirectCallback
        || (async () => undefined),
    };
  }, []);

  return <Auth0Context.Provider value={mock}>{children}</Auth0Context.Provider>;
}

export function Auth0ProviderWithNavigate({ children }) {
  const navigate = useNavigate();
  const playwrightMock = getPlaywrightAuth0Mock();

  const onRedirectCallback = useCallback((appState) => {
    applyAuthReturnTo(navigate, appState?.returnTo, { flushSync });
  }, [navigate]);

  if (playwrightMock) {
    return <MockAuth0Provider>{children}</MockAuth0Provider>;
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: auth0Audience,
        scope: 'openid profile email offline_access',
      }}
      onRedirectCallback={onRedirectCallback}
    >
      <AuthRedirectGate>
        {children}
      </AuthRedirectGate>
    </Auth0Provider>
  );
}

export default Auth0ProviderWithNavigate;
